import { List, Record, Map, mergeWith } from "immutable";
import unionby from "lodash.unionby";

import { defaultValues } from "@config/defaultValues";

import { AVAILABLE_CURRENCIES, VAT_INFO } from "@typings/globals";
import {
  Task,
  EXTRA_SERVICES_B2B,
  B2BTasks,
  Agent,
  AgentRaw,
  PremisesEquipments,
  PREMISES,
  PREMISES_BOOLEAN_EQUIPMENTS_OPTIONS,
  PREMISES_COUNTABLE_EQUIPMENTS_OPTIONS,
  FREQUENCY,
  B2BPricing,
  B2BPricingRaw,
  PremisesServicesRaw,
  Service,
  PremisesServices,
  PremisesService,
  FrequenciesDto,
} from "@typings/b2b";
import {
  DayItemRaw,
  HourItem,
  PART_OF_DAY,
  BookingAvailability,
  BookingAvailabilityRaw,
  CALENDAR_TYPE,
  PricePlan,
} from "@typings/booking";
import {
  COUNTRY_ISO_ENUM,
  Location,
  LocationsCollection,
} from "@typings/locations";
import { TYPE_OF_SUBPARTS } from "@typings/priceSubparts";

import {
  generateStaticDays,
  generateStaticBookingHours,
  generateStaticBookingHoursForB2BAdmins,
  genericCustomStaticDays,
} from "@services/StaticTimeGenerators";
import { getFullCalendarDate, replaceTimeZone } from "@services/DateFormatter";

const {
  b2b: { tasks, frequency },
} = defaultValues;

const {
  DESKS_EMPLOYEES,
  TOILETS,
  MEETING_ROOMS,
  CONSULTATION_ROOMS,
  WAITING_ROOMS,
  SHOWERS,
  LOCKERS,
  CLASSROOMS,
} = PREMISES_COUNTABLE_EQUIPMENTS_OPTIONS;

const {
  FULLY_EQUIPPED_KITCHEN,
  CAFETERIA,
  BATHROOM_WITH_SHOWER,
  INVENTORY_STOCK,
  CANTEEN,
} = PREMISES_BOOLEAN_EQUIPMENTS_OPTIONS;

export const premisesCountableEquipments = {
  [PREMISES.OFFICE]: [DESKS_EMPLOYEES, TOILETS, MEETING_ROOMS],
  [PREMISES.MEDICAL_PRACTICE]: [TOILETS, CONSULTATION_ROOMS, WAITING_ROOMS],
  [PREMISES.FITNESS_OR_YOGA_CENTER]: [TOILETS, SHOWERS, LOCKERS],
  [PREMISES.SCHOOL_OR_KINDERGARTEN]: [TOILETS, LOCKERS, CLASSROOMS],
  [PREMISES.WELLNESS_CENTER]: [TOILETS, SHOWERS],
  [PREMISES.SHOP]: [TOILETS, SHOWERS],
  [PREMISES.FREELANCE]: [DESKS_EMPLOYEES, TOILETS, SHOWERS],
  [PREMISES.OTHER]: [DESKS_EMPLOYEES, TOILETS, MEETING_ROOMS],
};
export const premisesBooleanEquipments = {
  [PREMISES.OFFICE]: [FULLY_EQUIPPED_KITCHEN, CAFETERIA, BATHROOM_WITH_SHOWER],
  [PREMISES.MEDICAL_PRACTICE]: [CAFETERIA],
  [PREMISES.FITNESS_OR_YOGA_CENTER]: [CAFETERIA],
  [PREMISES.SCHOOL_OR_KINDERGARTEN]: [CANTEEN],
  [PREMISES.WELLNESS_CENTER]: [CAFETERIA, INVENTORY_STOCK],
  [PREMISES.SHOP]: [CAFETERIA, INVENTORY_STOCK],
  [PREMISES.FREELANCE]: [FULLY_EQUIPPED_KITCHEN, BATHROOM_WITH_SHOWER],
  [PREMISES.OTHER]: [FULLY_EQUIPPED_KITCHEN, CAFETERIA, BATHROOM_WITH_SHOWER],
};

export const taskFactory = (input: Task): Record<Task> =>
  Record<Task>({ ...input })();

export const b2bTasks = Record<B2BTasks>({
  [EXTRA_SERVICES_B2B.B2B_CLEANING]: taskFactory({
    service: EXTRA_SERVICES_B2B.B2B_CLEANING,
    duration: tasks[EXTRA_SERVICES_B2B.B2B_CLEANING].duration,
    selected: tasks[EXTRA_SERVICES_B2B.B2B_CLEANING].selected,
  }),
})();

export const premisesServicesFactory = (
  premisesServicesRaw: PremisesServicesRaw[],
): PremisesServices => {
  const premisesMap = premisesServicesRaw.reduce(
    (acc: PremisesServices, premisesServices: PremisesServicesRaw) => {
      const servicesMap = premisesServices.services.reduce(
        (acc: PremisesService, service: Service) => {
          const serviceAsRecordTask = taskFactory({
            service: service.name,
            duration: service.duration / 60, // parse minutes to hours
            selected: false,
          });

          return acc.set(service.name, serviceAsRecordTask);
        },
        Map(),
      );

      return acc.set(premisesServices.premises, servicesMap);
    },
    Map(),
  );

  return premisesMap;
};

export const pricingInitialState: B2BPricing = {
  pricesPerFrequency: List(
    Object.keys(FREQUENCY).map(key => ({
      frequency: key as FREQUENCY,
      amount: 0,
      selected: key === frequency,
    })),
  ),
  displayedPriceForFirstBooking:
    defaultValues.b2b.displayedPriceForFirstBooking,
  currency: defaultValues.b2b.currency,
  hasSpecialPricing: defaultValues.b2b.hasSpecialPricing,
  specialPricingTranslatedMessage:
    defaultValues.b2b.specialPricingTranslatedMessage,
  isFetched: false,
  discountsToDisplay: List(),
};

export const pricingFactory = (input: B2BPricingRaw) => {
  const {
    oncePricePerHour,
    weeklyPricePerHour,
    every2WeeksPricePerHour,
    every4WeeksPricePerHour,
    moreOftenPricePerHour,
    discountsToDisplay,
    ...rest
  } = input;

  const factoryInput: B2BPricing = {
    ...rest,
    pricesPerFrequency: List([
      {
        frequency: FREQUENCY.ONCE,
        amount: oncePricePerHour,
      },
      {
        frequency: FREQUENCY.EVERY_4_WEEKS,
        amount: every4WeeksPricePerHour,
      },
      {
        frequency: FREQUENCY.EVERY_2_WEEKS,
        amount: every2WeeksPricePerHour,
      },
      {
        frequency: FREQUENCY.WEEKLY,
        amount: weeklyPricePerHour,
      },
      {
        frequency: FREQUENCY.MORE_OFTEN,
        amount: moreOftenPricePerHour,
      },
    ]),
    discountsToDisplay: List(
      (Array.isArray(discountsToDisplay) ? discountsToDisplay : []).map(
        ({ type, ...subpart }) => ({
          ...subpart,
          type: type as TYPE_OF_SUBPARTS,
          currency: rest.currency,
        }),
      ),
    ),
  };

  return Record<B2BPricing>(pricingInitialState)(factoryInput);
};

export const premisesEquipmentsInitialState = {
  numberOfDesks: 0,
  numberOfToilets: 0,
  numberOfMeetingRooms: 0,
  numberOfConsultationRooms: 0,
  numberOfWaitingRooms: 0,
  numberOfShowers: 0,
  numberOfLockers: 0,
  numberOfClassrooms: 0,
  hasFullyEquippedKitchen: false,
  hasCafeteria: false,
  hasBathroomWithShower: false,
  hasCanteen: false,
  hasInventory: false,
};

export const premisesEquipmentsFactory = (
  premisesEquipments: PremisesEquipments,
): Record<PremisesEquipments> =>
  Record(premisesEquipmentsInitialState)({
    ...premisesEquipments,
  });

const agentFactory = (agent: AgentRaw): Record<Agent> =>
  Record({
    firstName: "",
    lastName: "",
    uuid: "",
    isPrevious: false,
    label: "",
  })({
    firstName: agent.firstName,
    lastName: agent.lastName,
    uuid: agent.uuid,
    isPrevious: agent.isPrevious,
    label: `${agent.firstName} ${agent.lastName}`,
  });

export const agentsFactory = (agents: AgentRaw[]): List<Record<Agent>> =>
  List(agents.map(agentFactory));

const hoursMapFactory = (input: DayItemRaw[], fromApi?: boolean) =>
  input.reduce(
    (
      acc: Map<string, List<HourItem>>,
      curr: DayItemRaw,
    ): Map<string, List<HourItem>> => {
      return acc.set(
        curr.day,
        List(
          curr.hours.map((item): HourItem => {
            const hour = item.time.split(":")[0];
            let partOfDay: PART_OF_DAY = PART_OF_DAY.MORNING;

            if (Number(hour) >= 12 && Number(hour) < 17) {
              partOfDay = PART_OF_DAY.AFTERNOON;
            } else if (Number(hour) >= 17) {
              partOfDay = PART_OF_DAY.EVENING;
            }

            // availableAgentsRemaining is an optional API field,
            // which is returned only if there's less than 3 agents
            // instead of having an optional field on the frontend
            // we stub it with relevant value
            return {
              duration: item.time,
              availableAgentsRemaining:
                item.availableAgentsRemaining || (fromApi ? 3 : 0),
              partOfDay,
            };
          }),
        ),
      );
    },
    Map(),
  );

export const bookingAvailabilityInitialState: BookingAvailability = {
  defaultSelection: "",
  canSelectPreviousWeek: true,
  canSelectNextWeek: true,
  weekStartDate: "",
  customDays: null,
  days: List(generateStaticDays()),
  hours: hoursMapFactory(
    generateStaticDays().map(day => ({
      day,
      hours: generateStaticBookingHours(),
      timeslots: [],
    })),
  ),
  calendarType: CALENDAR_TYPE.HOURS,
  hasNoAvailableDates: true,
};

const getAnyDateAvailableV2 = (
  input: BookingAvailability["hours"],
): string | null => {
  const hours = input.toJS() as { [key: string]: HourItem[] };

  for (const key in hours) {
    const availableHour = hours[key].find(
      hour => hour.availableAgentsRemaining !== 0,
    );

    if (!availableHour) continue;

    return getFullCalendarDate(key, availableHour.duration);
  }

  return null;
};

/**
 * The /booking/availability endpoint only returns days with available agents.
 * Because of that we need to generate the calendar days on the frontend
 * and then update them with the availability data from the API.
 */

interface BookingAvailabilityFactory {
  input: BookingAvailabilityRaw;
  isUserAdmin: boolean;
}

export const bookingAvailabilityFactory = ({
  input,
  isUserAdmin,
}: BookingAvailabilityFactory): Record<BookingAvailability> => {
  // If somehow `input` is `undefined`
  // It's happening for E2E tests
  if (!Object.keys(input).length) {
    return Record<BookingAvailability>(bookingAvailabilityInitialState)();
  }

  const staticDays = generateStaticDays(input.weekStartDate);
  const staticHours: DayItemRaw[] = staticDays.map(day => ({
    day,
    timeslots: [],
    hours: isUserAdmin
      ? generateStaticBookingHoursForB2BAdmins()
      : generateStaticBookingHours(),
  }));

  const apiHoursMap = hoursMapFactory(input.days, true);
  const placeholderHoursMap = hoursMapFactory(staticHours);

  const mergedHours = mergeWith<Map<string, List<HourItem>>>(
    (oldVal, newVal) =>
      List(unionby(newVal.toJS(), oldVal.toJS(), "duration")).sortBy(
        (item: any) => item.duration,
      ),
    placeholderHoursMap,
    apiHoursMap,
  );
  const defaultSelection = input.defaultSelection
    ? input.defaultSelection
    : getAnyDateAvailableV2(mergedHours);

  return Record<BookingAvailability>(bookingAvailabilityInitialState)({
    ...input,
    hours: mergedHours,
    days: List(staticDays),
    hasNoAvailableDates: !input.days.length,
    defaultSelection: replaceTimeZone(defaultSelection),
  });
};

export const bookingCustomAvailabilityFactory = (
  input: BookingAvailabilityRaw,
  currentState: Record<BookingAvailability>,
  chunkSize = 3,
  isExtraRequest?: boolean,
): Record<BookingAvailability> => {
  // If somehow `input` is `undefined`
  // It's happening for E2E tests
  if (!Object.keys(input).length) {
    return Record<BookingAvailability>(bookingAvailabilityInitialState)();
  }

  const staticDays: string[] = generateStaticDays(input.weekStartDate);
  const staticHours = staticDays.map(
    (day): DayItemRaw => ({
      day,
      hours: generateStaticBookingHours(),
    }),
  );

  const apiHoursMap = hoursMapFactory(input.days, true);
  const currentHoursMap = mergeWith<Map<string, List<HourItem>>>(
    (oldVal, newVal) =>
      List(unionby(newVal.toJS(), oldVal.toJS(), "duration")).sortBy(
        (item: any) => item.duration,
      ),
    currentState.get("hours"),
    hoursMapFactory(staticHours),
  );

  const mergedHours = mergeWith<Map<string, List<HourItem>>>(
    (oldVal, newVal) =>
      List(unionby(newVal.toJS(), oldVal.toJS(), "duration")).sortBy(
        (item: any) => item.duration,
      ),
    currentHoursMap,
    apiHoursMap,
  );

  let customDays = currentState.get("customDays");
  const customDaysLenght = Object.keys(customDays || {}).length;

  if (
    !customDays ||
    (!!customDaysLenght && customDaysLenght % chunkSize !== 0)
  ) {
    customDays = genericCustomStaticDays(chunkSize);
  }

  const defaultSelection = input.defaultSelection
    ? input.defaultSelection
    : getAnyDateAvailableV2(mergedHours);

  return Record<BookingAvailability>(bookingAvailabilityInitialState)({
    ...input,
    weekStartDate: isExtraRequest
      ? currentState.get("weekStartDate")
      : input.weekStartDate,
    hours: mergedHours,
    days: List(staticDays),
    calendarType: CALENDAR_TYPE.HOURS,
    defaultSelection: replaceTimeZone(defaultSelection),
    hasNoAvailableDates: false,
    customDays,
  });
};

const initialLocation = Record<Location>({
  uuid: "",
  zip: "",
  city: "",
  address: "",
  state: "",
  country: "",
  countryIso: COUNTRY_ISO_ENUM.CH,
  firstName: "",
  lastName: "",
  companyName: "",
  entryCode: "",
  keyPick: "",
  howToGetIn: "",
  hasPets: false,
  previousJobs: 0,
  upcomingJobs: 0,
  ongoingJobs: 0,
  hideCityName: false,
  hasActiveContract: true,
});

export const locationsFactory = (input: Location[]): LocationsCollection => {
  const locations = input.reduce(
    (acc: LocationsCollection, location: Location) =>
      acc.set(location.uuid, initialLocation(location)),
    Map(),
  );

  return locations;
};

export const frequenciesFactory = (input: FrequenciesDto): List<FREQUENCY> => {
  return List(input.availableFrequencies);
};

export const pricePlanFactory = (
  selectedFrequency: FREQUENCY,
  availablePlans: List<Record<PricePlan>>,
  serviceLocationId?: number,
): string | null => {
  // if user has active subscription we should assign ONCE jobs
  // to subscription with `useForOnceBooking` flag
  if (selectedFrequency === FREQUENCY.ONCE && serviceLocationId) {
    return (
      availablePlans.find(plan => plan.get("useForOnceBooking"))?.get("uuid") ||
      null
    );
  }

  const allAvailablePlansForFrequency = availablePlans.filter(
    plan => plan.get("frequency") === selectedFrequency,
  );

  return allAvailablePlansForFrequency.get(0)?.get("uuid") || null;
};

const pricePlanInitialState: PricePlan = {
  uuid: "",
  frequency: "",
  name: "",
  description: "",
  commitmentMonthsDuration: 0,
  freeCancellations: 0,
  pricePerHour: 0,
  hidden: false,
  useForOnceBooking: false,
  currency: AVAILABLE_CURRENCIES.CHF,
  pricePerHourVatInfo: VAT_INFO.NO_INFO,
};

export const pricePlansFactory = (
  plans: PricePlan[],
): List<Record<PricePlan>> => {
  return List(plans.map(plan => Record(pricePlanInitialState)(plan)));
};
